import React, { useMemo } from 'react';
import { ThemeProvider } from 'styled-components';
import { useGoogleFonts } from '@flyyer/use-googlefonts';
import useDarkMode from '@retell/use-dark-mode';

import themeJSON from './theme.json';

const mergeObjects = (a, b) => Object.fromEntries([
  ...Object.entries(a).filter(([k]) => !(k in b)),
  ...Object.entries(b).filter(([k]) => !(k in a)),
  ...Object.entries(b).filter(([k]) => (k in a)).map(
    ([k, v]) => [k, Object(v) === v && Object(a[k]) === a[k]
      ? mergeObjects(a[k], v) : v]
  ),
]);

const DEFAULTS = {
  theme: 'light',
  aspectRatio: 'square',
  parallax: 'none',
  roundness: 'rounded',
  lang: {
    key: 'en',
    dir: 'ltr',
  },
  imageSizing: {
    card: 'cover',
    gallery: 'cover',
  },
  fonts: {
    en: {
      primary: {
        family: 'Montserrat',
        weight: {
          normal: 400,
          bold: 600,
        },
        scaling: 1,
      },
      secondary: {
        family: 'Montserrat',
        weight: {
          normal: 400,
          bold: 600,
        },
        scaling: 1,
      },
    },
    ar: {
      primary: {
        family: 'Cairo',
        weight: {
          normal: 500,
          bold: 700,
        },
        scaling: 1,
      },
      secondary: {
        family: 'Cairo',
        weight: {
          normal: 500,
          bold: 700,
        },
        scaling: 1,
      },
    },
  },
};

const StoreCustomCSS = ({ css, brandColor }) => (
  <style>
    {brandColor ? `:root { --brand-color: ${brandColor}; }` : ''}
    {css}
  </style>
);

const StoreThemeProvider = ({ children, data }) => {
  const themeObject = useMemo(
    () => mergeObjects(themeJSON, data?.styleThemeObject || {}),
    [data]
  );

  const storeFonts = useMemo(
    () => ({
      // Title
      titleFamily: data?.styleFontTitle?.family?.replaceAll?.('+', ' ')
        || DEFAULTS.fonts[data?.language?._id || DEFAULTS.lang.key]
          .primary.family,
      titleWeightNormal: data?.styleFontTitle?.variantNormal
        || DEFAULTS.fonts[data?.language?._id || DEFAULTS.lang.key]
          .primary.weight.normal,
      titleWeightBold: data?.styleFontTitle?.variantBold
        || DEFAULTS.fonts[data?.language?._id || DEFAULTS.lang.key]
          .primary.weight.bold,
      titleScaling: data?.styleFontTitle?.scaling
        || DEFAULTS.fonts[data?.language?._id || DEFAULTS.lang.key]
          .primary.scaling,
      // Body
      bodyFamily: data?.styleFontBody?.family?.replaceAll?.('+', ' ')
        || DEFAULTS.fonts[data?.language?._id || DEFAULTS.lang.key]
          .secondary.family,
      bodyWeightNormal: data?.styleFontBody?.variantNormal
        || DEFAULTS.fonts[data?.language?._id || DEFAULTS.lang.key]
          .secondary.weight.normal,
      bodyWeightBold: data?.styleFontBody?.variantBold
        || DEFAULTS.fonts[data?.language?._id || DEFAULTS.lang.key]
          .secondary.weight.bold,
      bodyScaling: data?.styleFontBody?.scaling
        || DEFAULTS.fonts[data?.language?._id || DEFAULTS.lang.key]
          .secondary.scaling,
    }),
    [data],
  );

  useGoogleFonts([
    {
      family: storeFonts.titleFamily,
      styles: (
          storeFonts.titleWeightNormal === storeFonts.titleWeightBold
        ? [storeFonts.titleWeightNormal]
        : [
            storeFonts.titleWeightNormal,
            storeFonts.titleWeightBold,
          ]
      ),
    },
    {
      family: storeFonts.bodyFamily,
      styles: (
        storeFonts.bodyWeightNormal === storeFonts.bodyWeightBold
        ? [storeFonts.bodyWeightNormal]
        : [
          storeFonts.bodyWeightNormal,
          storeFonts.bodyWeightBold,
        ]
      ),
    },
  ]);

  const { value: isDarkMode } = useDarkMode();
  const currentTheme = data?.styleTheme === 'auto'
    ? isDarkMode
      ? 'dark'
      : 'light'
    : data?.styleTheme || DEFAULTS.theme;

  const theme = useMemo(
    () => ({
      theme: currentTheme,
      sitewidth: themeObject.siteWidth,
      padding: themeObject.padding,
      breakpoints: themeObject.breakpoints,
      lang: {
        key: data?.language?._id || DEFAULTS.lang.key,
        dir: data?.language?.direction || DEFAULTS.lang.dir,
      },
      fonts: {
        primary: {
          family: storeFonts.titleFamily,
          weight: {
            normal: storeFonts.titleWeightNormal,
            bold: storeFonts.titleWeightBold,
          },
          scaling: storeFonts.titleScaling,
        },
        secondary: {
          family: storeFonts.bodyFamily,
          weight: {
            normal: storeFonts.bodyWeightNormal,
            bold: storeFonts.bodyWeightBold,
          },
          scaling: storeFonts.bodyScaling,
        },
      },
      card: themeObject.card,
      gallery: themeObject.gallery,
      slider: themeObject.slider,
      colors: {
        ...themeObject.colors[currentTheme],
        brand: data?.brandColor || themeObject.colors[currentTheme].brand,
        title: data?.brandColorUseForTitle
          ? data?.brandColor
          : themeObject.colors[currentTheme].title,
      },
      imageSizing: {
        card: data?.imageSizing?.toLowerCase()
          || DEFAULTS.imageSizing.card,
        gallery: data?.galleryimageSizing?.toLowerCase()
          || DEFAULTS.imageSizing.gallery,
      },
      aspectRatio: themeObject.aspectRatio[
        data?.styleImageAspectRatio || DEFAULTS.aspectRatio
      ],
      parallax: themeObject
        .parallax[data?.styleImageParallax || DEFAULTS.parallax],
      roundness: themeObject
        .roundness[data?.styleRoundness || DEFAULTS.roundness],
    }),
    [data, themeObject, storeFonts, currentTheme],
  );

  return (
    <ThemeProvider theme={theme}>
      <StoreCustomCSS
        css={data?.styleCss || ''}
        brandColor={data?.brandColor
          || themeObject.colors[currentTheme].brand || ''}
      />
      {children}
    </ThemeProvider>
  );
};

export default StoreThemeProvider;
